// 3-preset direction picker — shared by the wizard and the profile
// modal. The underlying state is still a 0-100 number (dir_ratio),
// kept intact for backward compat and for the picker logic in
// session.ts; the UI just constrains the user's choice to 3 clean
// options.
//
// Labels reflect the deck's actual faces (e.g. "FR → BG" for a
// bilingual general deck, "pays → capitale" for capitales) instead of
// the old language-centric "Lecture / Écriture" framing, which didn't
// read right outside of translation contexts.

import { t } from "../i18n";
import { escapeHtml } from "./html";

/** Pure presets: 0 = always face[1]→face[0], 50 = balanced mix,
 *  100 = always face[0]→face[1]. The directional pills are labelled
 *  with the actual face arrows ("pays → capitale"), so the user
 *  expects a clean single-direction behaviour when they click one —
 *  a mid-range value (20/80) would silently contradict the label by
 *  mixing 80/20. Users who want a mix pick "équilibré". */
export const DIR_PRESETS = [
  { key: "read", value: 0 },
  { key: "balanced", value: 50 },
  { key: "write", value: 100 },
] as const;

/** Face-label pair for a deck: [face[0] short name, face[1] short
 *  name]. Used to build the directional preset labels — e.g.
 *  ["FR", "BG"] yields "FR → BG" / balanced / "BG → FR". */
export type DirFaceLabels = readonly [string, string];

/** Map an arbitrary 0-100 ratio to the nearest preset value. Used to
 *  highlight the "active" pill for users whose stored value came
 *  from the old free-slider (or from another device). */
export function presetFromRatio(ratio: number): number {
  // `as const` narrows each value to a literal type — widen to
  // `number` here so the loop can reassign across different presets
  // without TS complaining about 50 not being assignable to 20.
  let best: number = DIR_PRESETS[0].value;
  let bestDist = Math.abs(ratio - best);
  for (const p of DIR_PRESETS) {
    const d = Math.abs(ratio - p.value);
    if (d < bestDist) {
      best = p.value;
      bestDist = d;
    }
  }
  return best;
}

/** Render the 3 pills into `host` and wire each click to `onPick`.
 *  Safe to call repeatedly — each render wipes the host's innerHTML.
 *
 *  `faces` drives the pill labels: the 20% pill shows `face[1] → face[0]`
 *  (majority direction at that ratio), the 50% pill shows a neutral
 *  "≈" + balanced keyword, the 80% pill shows `face[0] → face[1]`. */
export function renderDirPresets(
  host: HTMLElement,
  currentRatio: number,
  onPick: (value: number) => void,
  faces: DirFaceLabels,
): void {
  const active = presetFromRatio(currentRatio);
  const [a, b] = faces;
  const arrow = t("card_arrow") as string;
  // Cache the "write target" preset value so labelFor stays correct
  // if DIR_PRESETS ever moves again — the "write" key by convention
  // is face[0]→face[1]; anything else is the reverse direction.
  const writePreset = DIR_PRESETS.find((p) => p.key === "write")!.value;
  const labelFor = (value: number): string => {
    if (value === 50) return t("dir_preset_balanced") as string;
    // write = face[0]→face[1]; read = face[1]→face[0].
    const [from, to] = value === writePreset ? [a, b] : [b, a];
    return `${escapeHtml(from)} ${arrow} ${escapeHtml(to)}`;
  };
  host.innerHTML = DIR_PRESETS.map(
    (p) =>
      `<button type="button" class="dir-preset-pill${p.value === active ? " active" : ""}" data-dir="${p.value}">${labelFor(p.value)}</button>`,
  ).join("");
  host.querySelectorAll<HTMLButtonElement>(".dir-preset-pill").forEach((btn) => {
    btn.addEventListener("click", () => {
      const v = parseInt(btn.dataset.dir || "50", 10);
      onPick(v);
      renderDirPresets(host, v, onPick, faces);
    });
  });
}
